import React, { useEffect, useState } from "react";
import { Clock, Search } from "lucide-react";
import { RootState, useAppDispatch } from "../../../redux/store";
import { GoogleMap, LoadScript, Marker, Polyline } from "@react-google-maps/api";
import { useLocation } from "react-router-dom";
import { getGeoTrackingDetails } from "../../../redux/features/geoTracking";
import { useSelector } from "react-redux";
import {
  formatDateTime12hour,
} from "../../../utils/employeeTimeCardhelper";
import { GeoLogService } from "../../../services";
import { GeoLog } from "../../../schemas/geoLog";
const ViewTrackingMapPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [geoLogs, setGeoLogs] = useState<GeoLog[]>([]);
  const [loading, setLoading] = useState(false);
  const { geoTrackingDetails } = useSelector(
    (state: RootState) => state?.geoTracking
  );
  const { user } = useSelector((state: RootState) => state?.user);
  const trackingData = location?.state?.tracking_data;
  const apiKey = process.env.GOOGLE_MAPS_API_KEY ?? process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "";
  console.log("Google Maps API Key:", apiKey);
  const containerStyle = {
    width: "100%",
    height: "400px",
    borderRadius: "8px",
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  };
  // Calculate center based on geo logs or fallback to existing logic
  const center = geoLogs.length > 0 
    ? {
        lat: geoLogs[0].lat,
        lng: geoLogs[0].long,
      }
    : {
        lat: Number(
          geoTrackingDetails?.data?.[0]?.document?.fields?.lat?.stringValue
        ) || 0,
        lng: Number(
          geoTrackingDetails?.data?.[0]?.document?.fields?.long?.stringValue
        ) || 0,
      };

  // Create path for polyline from geo logs
  const pathCoordinates = geoLogs.map(log => ({
    lat: log.lat,
    lng: log.long
  }));

  // Polyline options
  const polylineOptions = {
    strokeColor: "#2563eb",
    strokeOpacity: 0.8,
    strokeWeight: 3,
  };

  useEffect(() => {
    dispatch(
      getGeoTrackingDetails({
        tracking_uuid: location.state?.tracking_uuid,
        employee_id: "", // optional - backend uses token if blank
      })
    );
  }, [dispatch, location.state.tracking_uuid]);

  // Fetch geo logs using the service
  useEffect(() => {
    const fetchGeoLogs = async () => {
      // if (!location.state.tracking_uuid) return;
      
      setLoading(true);
      try {
        // You'll need to provide user_account_id and emp_account_id
        // These should be available from your app state or tracking data
        const userAccountId = user?.user_account_id || ""; // Update this based on your data structure
        const empAccountId = trackingData?.emp_account_id || user?.os_emp_account_id || ""; // Update this based on your data structure
        
        if (userAccountId && empAccountId) {
          const logs = await GeoLogService.getAllByTrackingUuid(
            location.state.tracking_uuid,
            userAccountId,
            empAccountId
          );

          console.log("Fetched Geo Logs:", logs);
          
          // Sort logs by created_at to ensure proper order for path, handling optional created_at
          const sortedLogs = logs.sort((a, b) => {
            const aTime = a.created_at ? new Date(a.created_at).getTime() : Number.POSITIVE_INFINITY;
            const bTime = b.created_at ? new Date(b.created_at).getTime() : Number.POSITIVE_INFINITY;
            return aTime - bTime;
          });
          
          setGeoLogs(sortedLogs);
        }
      } catch (error) {
        console.error("Error fetching geo logs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchGeoLogs();
  }, [location.state.tracking_uuid, trackingData]);

  return (
    <div className="max-w-full mx-4 border rounded-xl mt-4 ">
      <div className="flex items-center space-x-2 m-2 px-4 pt-2">
        <div className="text-md font-medium text-blue-600">{trackingData?.emp_detail?.emp_name}</div>
      </div>
      <div className="flex items-center space-x-2 mb-2 px-4 pt-4">
        <Clock className="text-blue-600" />
        <div className="text-sm font-medium text-blue-600">Clock In</div>
        <div className="text-sm text-gray-600">
          {" "}
          {formatDateTime12hour(trackingData?.os_emp_start_datetime || "")}
        </div>
      </div>
      <hr className="my-3" />

      <div className="flex items-center space-x-2 mb-4 px-4">
        <Clock className="text-blue-600" />
        <div className="text-sm font-medium text-blue-600">Clock Out</div>
        <div className="text-sm text-gray-600">
          {" "}
          {formatDateTime12hour(trackingData?.os_emp_end_datetime || "")}
        </div>
      </div>

      {/* Geo logs info */}
      <div className="flex items-center space-x-2 mb-4 px-4">
        <div className="text-sm font-medium text-blue-600">
          Tracking Points: {geoLogs.length}
        </div>
        {loading && (
          <div className="text-sm text-gray-500">Loading...</div>
        )}
      </div>
      {/* <div className="flex items-center space-x-2 mb-4 px-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search"
            className="w-full pl-10 border rounded-md px-3 py-2"
          />
        </div>
      </div> */}

      <div className="px-4 pb-4 mt-24 mb-4">
        <div className="w-full h-60 bg-gray-200 rounded-md flex items-center justify-center relative">
          {/* <span className="text-gray-500 text-sm"> */}{" "}
          <div
            style={{
              height: "400px",
              width: "100%",
              marginTop: "10px",
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            }}
          >
            {/* google maps integration */}
            <LoadScript googleMapsApiKey={apiKey}>
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={13}
              >
                {/* Render markers for first and last geo log only */}
                {geoLogs.length > 0 && (
                  <>
                    {/* First marker (start point) */}
                    <Marker
                      key={`start-${geoLogs[0].id}`}
                      position={{ lat: geoLogs[0].lat, lng: geoLogs[0].long }}
                      title={`Start - ${geoLogs[0].created_at?.toLocaleString()}`}
                      icon={{
                        url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png",
                        scaledSize: new window.google.maps.Size(32, 32)
                      }}
                    />
                    
                    {/* Last marker (end point) - only if there's more than one point */}
                    {geoLogs.length > 1 && (
                      <Marker
                        key={`end-${geoLogs[geoLogs.length - 1].id}`}
                        position={{ 
                          lat: geoLogs[geoLogs.length - 1].lat, 
                          lng: geoLogs[geoLogs.length - 1].long 
                        }}
                        title={`End - ${geoLogs[geoLogs.length - 1].created_at?.toLocaleString()}`}
                        icon={{
                          url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
                          scaledSize: new window.google.maps.Size(32, 32)
                        }}
                      />
                    )}
                  </>
                )}
                
                {/* Draw polyline connecting all points */}
                {pathCoordinates.length > 1 && (
                  <Polyline
                    path={pathCoordinates}
                    options={polylineOptions}
                  />
                )}
                
                {/* Fallback marker if no geo logs */}
                {geoLogs.length === 0 && center.lat !== 0 && center.lng !== 0 && (
                  <Marker position={center} />
                )}
              </GoogleMap>
            </LoadScript>
          </div>
          {/* </span> */}
        </div>
      </div>
    </div>
  );
};

export default ViewTrackingMapPage;
